// app/javascript/compcalc/components/PermDisability.jsx

import React from "react";
import PropTypes from "prop-types";
import { withStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import MenuIcon from '@material-ui/icons/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import withErrorHandling from './ErrorHandling';
import { currencyFormatter } from '../utils';


const styles = theme => ({
  root: {
    flexGrow: 1,
    marginTop: theme.spacing(1),
  },
  group: {
    display: 'flex',
    flexFlow: 'row nowrap',
  },
  alignCenter: {
    // background: 'green',
    display: 'flex',
    justifyContent: 'center',    // Horizontal align.
    alignItems: 'center',        // Vertical align.
  },
  alignLeft: {
    // background: 'blue',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',        // Vertical align.
  },
  alignRight: {
    // background: 'red',
    display: 'flex',
    flexFlow: 'row-reverse nowrap',
    alignItems: 'center',
  },
  paper: {
    padding: theme.spacing(2),
    display: 'inline-block',
  },
  wrapContent: {
    padding: theme.spacing(2),
    display: 'inline-block',
  },
  pre: {
    width: '100%',
    maxWidth: '800px',
    whiteSpace: 'pre-wrap',
  }
});


class PermDisability extends React.PureComponent {

  constructor() {
    super();
    this.dates = [ '1996 - 2002', '2003', '2004', '2005*', '2006 - 2012*', '2013', '2014 - 2024' ];

    this.itemsDates = [];
    for (let i = 0; i < this.dates.length; ++i) {
      this.itemsDates.push(
        <MenuItem dense key={'date' + i} value={i}>{this.dates[i]}</MenuItem>
      );
    }

    this.itemsPercentages = [];
    for (let i = 0; i < 100; ++i) {
      this.itemsPercentages.push(
        <MenuItem dense key={'percent' + i} value={i}>{i}%</MenuItem>
      );
    }

    this.pdRateMaxText = 'PD Rate (maximum)';
    this.pdRateMinText = 'PD Rate (minimum)';
    this.pdRateText = 'PD Rate';

    this.state = {
      pd: 50, // 50%
      doi: 3, // 2005*
      increase: 1, // none +/- 15% increase
      increaseEnabled: true,
      inputAww: false,

      pdRateText: this.pdRateMaxText,
      aww: currencyFormatter.format(0),
      pdRate: currencyFormatter.format(0),

      weeks: 0,
      amount: 0,

      isExpanded: false,
    };
  }

  componentDidMount = () => {
    this.fetchPD();
  }

  onTextExpand = () => {
    this.setState({ isExpanded: !this.state.isExpanded });
  }

  fetchPD = () => {
    let row = this.state.pd;
    let indexDate = this.state.doi;
    let date = this.dates[indexDate].substring(0, 4) + '-01-01';
    let aww = this.state.inputAww ? currencyFormatter.parse(this.state.aww) : 0.0;

    let url = `api/v1/permanent_disability.json?disability=${row}&date=${date}&aww=${aww}`;

    this.props.fetch(url, results => {
      // console.log(results);
      this.calculate(results);
    }).catch(e => this.props.showError(e, 'Error obtaining permanent disability amount from server'));
  }

  calculate = (result) => {
    let weeks = result['weeks'], amount = result['amount'];

    let indexDate = this.state.doi;
    let increaseEnabled = indexDate == 3 || indexDate == 4;

    let increaseIndex = increaseEnabled ? this.state.increase : 1;

    let increase = (increaseIndex - 1) * 0.15; // -15%, 0, 15%.
    if (increase > 0.0) {
      let days = weeks * 7.0; // to days
      if (days > 60.0) {
        amount /= days; // amount per day.
        let newAmount = amount * 60.0;
        days -= 60.0; // first 60 days not paying increase
        increase *= amount;
        amount += increase; // amount per day increased
        newAmount += days*amount; // add other days with increase
        amount = newAmount;
      }
    }
    else {
      increase = amount * increase;
      amount += increase;
    }

    let pdRateText = result.pdRate <= result.minPDRate ? this.pdRateMinText :
                     result.pdRate >= result.maxPDRate ? this.pdRateMaxText : this.pdRateText;

    let newState = {
      pdRateText: pdRateText,
      pdRate: currencyFormatter.format(result.pdRate),
      weeks: weeks,
      amount: amount,
      increase: increaseIndex,
      increaseEnabled: increaseEnabled,
    };
    if (!this.state.inputAww || currencyFormatter.parse(this.state.aww) <= 0.0)
      newState.aww = currencyFormatter.format(result.maxAWW);
    this.setState(newState);
  }

  onSelectChange = (event) => {
    // console.log(event.target.name, event.target.value);
    this.setState({ [event.target.name]: event.target.value }, this.fetchPD); // Alternative componentDidUpdate().
  };

  onInputAwwChanged = (checked) => {
    if (this.state.inputAww == checked) { return; }
    this.setState({
      inputAww: checked
    }, this.fetchPD);
  }

  onAwwChanged = (value) => {
    if (!this.state.inputAww) return;
    let aww = currencyFormatter.parse(value);
    let pdRate = aww * 2.0/3.0;
    // console.log(`onAwwChanged(): ${aww}`);
    this.setState({
      aww: currencyFormatter.format(aww),
      pdRate: currencyFormatter.format(pdRate),
    }, this.fetchPD);
  }

  onPdRateChanged = (value) => {
    if (!this.state.inputAww) return;
    let pdRate = currencyFormatter.parse(value);
    // console.log(`onPdRateChanged(): ${pdRate}`);

    let aww = pdRate * 3.0/2.0;
    this.setState({
      aww: currencyFormatter.format(aww),
      pdRate: currencyFormatter.format(pdRate),
    }, this.fetchPD);
  }

  render() {
    const { classes } = this.props;

    return (
      <Grid container spacing={1} className={classes.root} justifyContent="center">
        <Grid item xs={4} className={classes.alignCenter}>
          {/* Permanent Disability */}
          <Paper className={classes.paper}>
            <FormControl>
              {/*<Typography variant="h6">Permanent Disability</Typography>*/}
              {/*<InputLabel htmlFor="pd-auto-width">Permanent Disability</InputLabel>*/}
              <FormLabel component="legend">Permanent Disability</FormLabel>
              {/*For select: MenuProps={ {anchorOrigin: { horizontal: 'right', vertical: 'top' } } }*/}
              <Select
                value={ this.state.pd }
                onChange={this.onSelectChange}
                input={<Input id="pd" name="pd" />}
                autoWidth
              >
                { this.itemsPercentages }
              </Select>
              {/*<FormHelperText>Auto width</FormHelperText>*/}
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={4}  className={classes.alignCenter}>
          {/* Date of Injury */}
          <Paper className={classes.paper}>
            <FormControl>
              {/*<Typography variant="h6">Date of Injury</Typography>*/}
              {/*<InputLabel htmlFor="pd-auto-width">Permanent Disability</InputLabel>*/}
              <FormLabel component="legend">Date of Injury</FormLabel>
              {/*For select: MenuProps={ {anchorOrigin: { horizontal: 'right', vertical: 'top' } } }*/}
              <Select
                value={ this.state.doi }
                onChange={this.onSelectChange}
                input={<Input id="doi" name="doi" />}
                autoWidth
              >
                { this.itemsDates }
              </Select>
              {/*<FormHelperText>Auto width</FormHelperText>*/}
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={4} className={classes.alignCenter}>
          {/* 15% Increase */}
          <Paper className={classes.paper}>
            <FormControl component="fieldset">
              <FormLabel component="legend">15% Increase / Decrease</FormLabel>
              <RadioGroup
                aria-label="Increase"
                name="increase"
                className={ classes.group }
                value={ this.state.increase.toString() }
                onChange={this.onSelectChange}
              >
                <FormControlLabel value="0" disabled={!this.state.increaseEnabled} control={<Radio color="primary" />} label="-15%" />
                <FormControlLabel value="1" disabled={!this.state.increaseEnabled} control={<Radio color="primary" />} label="none" />
                <FormControlLabel value="2" disabled={!this.state.increaseEnabled} control={<Radio color="primary" />} label="+15%" />
              </RadioGroup>
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={6}  className={classes.alignCenter}>
          {/* Input AWW */}
          <Paper className={classes.wrapContent}>
            <FormControl>
              <FormControlLabel style={{ marginRight: -14 }} control={
                <ListItem button dense key='inputAww'>
                  <ListItemSecondaryAction>
                    <Checkbox
                      checked={ this.state.inputAww }
                      onChange={ (e) => this.onInputAwwChanged(e.target.checked) }
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                  <ListItemText primary='Input AWW' />
                </ListItem>
              } />
              <TextField
                id="aww"
                value={ this.state.aww }
                onKeyPress={ e => e.key == 'Enter' && e.target.blur() }
                onChange={ (e) => this.setState({ aww: e.target.value, inputAww: true }) }
                onFocus={ (e) => e.target.select() }
                onBlur={ (e) => this.onAwwChanged(e.target.value) }
                className={ classes.textField }
                margin="dense"
                InputProps={{
                  readOnly: false,
                }}
              />
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={6}  className={classes.alignCenter}>
          {/* PD Rate */}
          <Paper className={classes.wrapContent}>
            <FormControl>
              <FormControlLabel style={{ marginRight: -14 }} control={
                <ListItem button dense key='inputPdRate'>
                  <ListItemSecondaryAction>
                    <Checkbox
                      checked={ this.state.inputAww }
                      onChange={ (e) => this.onInputAwwChanged(e.target.checked) }
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                  <ListItemText primary={ this.state.pdRateText } />
                </ListItem>
              } />
              <TextField
                id="pdRate"
                value={ this.state.pdRate }
                onKeyPress={ e => e.key == 'Enter' && e.target.blur() }
                onChange={ (e) => this.setState({ pdRate: e.target.value, inputAww: true }) }
                onFocus={ (e) => e.target.select() }
                onBlur={ (e) => this.onPdRateChanged(e.target.value) }
                className={ classes.textField }
                margin="dense"
                InputProps={{
                  readOnly: false,
                }}
              />
            </FormControl>
          </Paper>
        </Grid>

        <Grid item xs={6}  className={classes.alignCenter}>
          {/* Weeks of Payment */}
          <Paper className={classes.paper}>
          <FormControl>
            <FormLabel component="legend">Weeks of Payment</FormLabel>
            {/*label="Weeks of Payment"*/}
            <TextField
              id="weeks"
              value={this.state.weeks.toString()}
              className={ classes.textField }
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </FormControl>

          </Paper>
        </Grid>

        <Grid item xs={6} className={classes.alignCenter}>
          {/* Amount */}
          <Paper className={classes.paper}>
            <FormLabel component="legend">Amount</FormLabel>
            {/*label="Amount"*/}
            <TextField
              id="amount"
              value={currencyFormatter.format(this.state.amount)}
              className={ classes.textField }
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </Paper>
        </Grid>


        <Grid item xs={12} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <div className={classes.pre}>
              <Typography variant="body1">
                {pdText1}
              </Typography>
              {this.state.isExpanded && (
                <div>
                  <br/>
                  <Typography variant="h6">
                    {pdText2}
                  </Typography>
                  <br/>
                  <Typography variant="body1">
                    {pdText3}
                  </Typography>
                </div>
              )}
              <div className={classes.alignRight}>
                <Button color="primary" onClick={this.onTextExpand}>
                    {this.state.isExpanded ? 'Less ...' : 'More ...'}
                </Button>
              </div>
            </div>
          </Paper>
        </Grid>

      </Grid>
    );
  }

};

export default withStyles(styles)(withErrorHandling(PermDisability));

const pdText1 = `* For injuries 1/1/05 through 12/31/12, the amount of each remaining weekly permanent disability indemnity payment is subject to a 15% decrease/increase per Labor Code Section 4658(d)(2)(3)

§ 4658.  Permanent disability; Computation; Benefits schedules; Increase or decrease in disability payments depending on offer or termination of regular, modified or alternative work.

§ 4660.1.  Percentages of permanent disability for injuries occurring on or after January 1, 2013; How determined; Adjusted rating schedule; Amendments and revisions to schedule.

§ 4662.  Permanent disabilities conclusively presumed to be total disability.

§ 4663.  Apportionment of permanent disability; Causation as basis; Physician's report; Apportionment determination; Disclosure by employee; Applicability.

§ 4664.  Liability of employer for percentage of permanent disability directly caused by injury; Conclusive presumption from prior award of permanent disability; Accumulation of permanent disability awards.`;

const pdText2 = `Current Statement/Summary of the Law Under Guzman/Ogilivie`;
const pdText3 = `The application and analysis under Guzman requires:
(1) that the reporting physician provide the STANDARD RATING under the standard method, chapter, or table from the 5th edition of the AMA Guides; Then the physician must:
(2) address whether the standard rating is ACCURATE, ie. complete;
(3) if the standard rating is not ACCURATE, what is it about the standard rating which makes it NOT ACCURATE OR INCOMPLETE; and
(4) where the standard rating is not accurate, the reporting physician may utilize an ALTERNATE METHOD, CHAPTER OR TABLE WITHIN THE FOUR CORNERS OF THE 5th Edition of the AMA Guides.
 In addressing the Guzman issue the analysis by the reporting physician is critical.  The focus must be on the OBJECTIVE FINDINGS with appropriate consideration given to subjective complaints.  It is the objective findings which will support and validate the subjective complaints ensuring a proper/correct diagnosis.  It is that diagnosis which will lead the reporting physician to the proper chapter and table to utilize the correct method for the standard rating.

On the issue of WHETHER THE STANDARD RATING IS ACCURATE the physician may only focus on the impact the injury has had on Activities of Daily Living, and not work preclusions.  Further, under the AMA guidelines it is the  OBJECTIVES which drive the analysis, but subjectives which are verifiable, reproducible and medically evidenced based may justify an alternate rating under an alternative method, chapter or table determined to be the MOST ACCURATE RATING.  In addressing the issue of why an alternate method is justified the physician must focus on the “how and why”, provided a “considered and reasoned analysis” to satisfy the substantial evidence requirement under a Guzman anlysis. (See, GUZMAN II:  Milpitas Unified School District, Keenan Associates v. WCAB (Guzman),  (2010, Sixth Appellate District) 187 Cal.App.4th 808; City of Sacramento v. WCAB (Cannon) (2013, 3rd Appellate District) 222 Cal.App.4th 1360, 2013 Cal.App.Lexis 1078, 79 CCC 1;)

Whereas a rating under the Guzman analysis is an attack on the accuracy of the Whole Person Impairment under a standard rating, the Ogilivie analysis is an attack on the accuracy of the DFEC modifier generally through the use of expert vocational evidence.  A rating under Ogilvie appears to require (1) expert vocational evidence establishing that (2) the impact which the impairment has on injured workers amenability to rehabilitation and therefore upon the injured worker’s Diminished Future Earning Capacity establishing that the DFEC is greater than the scheduled DFEC. While the focus of the VR evidence/opinion should be on impact on ADL’s, the VR opinion may arguably consider the impact on the injured worker’s ability to perform work.  Where the scheduled DFEC is not an accurate reflection of the extent to which applicant’s access to the labor market is limited, the Board may consider VR evidence in making an award of PD based solely on the workers diminished loss of future earning capacity under a quasi-Leboeuf analysis; “Ogilboeuf”.

However, even under an Ogilivie analysis the employer should not be liable for PD which is not the direct cause of the industrial injury.  This would exclude the injured worker’s DFEC attributable to non-industrial causation such as a lack of education, language deficiencies, and both pre-existing and non-industrial physical disability.

In the end the Ogilivie analysis appear to be morphing into Leboeuf analysis to rebut the scheduled DFEC with a focus on “synergy” among all industrial caused conditions.  (See, City and County of San Francisco v. WCAB (Ogilvie) (1st Appellate District, 2011) 197 Cal.App.4th 1262, 76 CCC 624, 2011 Cal. App. LEXIS 988; 76 Cal. Comp. Cases 624; Dahl v. Contra Costa County (Panel Decision) 40 CWCR 117, 2012 Cal. Wrk. Comp. P.D. LEXIS 173; Barrett Business Services, Inc., PSI v. Workers' Compensation Appeals Board, (Gallagher) (Court of Appeal, 2nd Appellate District) 78 Cal. Comp. Cases 1318; 2013 Cal. Wrk. Comp. LEXIS 187; Burke, dba Pride and Joy, v. WCABWorkers (Burton), (2014 1st Appellate District) 79 Cal. Comp. Cases 713; 2014 Cal. Wrk. Comp. LEXIS 65 (Writ Denied);`;
